import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useUser } from '@clerk/clerk-react';

import { ContentViewData } from '../../../types.ts/story';
import ContentShare from '../../common/ContentShare';
import SpinningLoading from '../../SpinningLoading';
import { BlogOrEmailContent } from '../../../types.ts/general';
import ContentSelector from '../../common/ContentSelector';
import { useFlagsCombination } from '../../../utility/useFlagsCombination';
import { BlogProducer } from './BlogProducer';
import SavedBlogList from './SavedBlogList';
import { initializeContentStudioContent } from '../../../utility/general';
import ChatGPTService from '../../../services/ChatGPTService';
import BlogEditor from './BlogEditor';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';

import { analytics } from '@src/utility/analytics';
import { AIPrompt } from '@src/types.ts/ai_prompts';
import {
  useAnalytics,
  useDatoClient,
  useTemplatedPrompts,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import AIProducerModal from '../AIProducerModal';
import ContentActions from '../ContentActions';
import BrandVoiceRevisionModal from '../BrandVoiceRevisionModal';
import { v4 as uuid } from 'uuid';
import { format } from 'date-fns';
import { Speaker } from '../SpeakerSelector';
const getSocialPostTitleFromTemplate = (template: AIPrompt) =>
  `${template.title} - ${format(new Date(), 'yyyy-MM-dd')}`;

const BlogView = observer(
  ({ type = 'blog' }: { type: 'blog' | 'socialPost' }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSpeakers, setSelectedSpeakers] = useState<Speaker[]>([]);
    const [contentHtml, setContentHtml] = useState<string | null>(null);
    const videoCreator = useVideoCreatorStore();
    const userIdentity = useUserIdentity();
    const datoClientStore = useDatoClient();
    const gptServiceRef = useRef(
      new ChatGPTService(videoCreator, datoClientStore.aiPromptRepository),
    );
    const gptService = gptServiceRef.current;

    const { story } = videoCreator;
    const [dragPosition, setDragPosition] = useState<Record<
      string,
      Record<'xAxis' | 'yAxis', string>
    > | null>(null);
    const [imageSize, setImageSize] = useState<Record<
      string,
      Record<'width' | 'height', number>
    > | null>(null);
    const [isResizing, setIsResizing] = useState<string | null>(null);
    const [openDropdown, toggleDropdown] = useState<number | null>(null);
    const [editEnabled, setEditEnabled] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [pageHash, setPageHash] = useState<string | null>(null);

    const userInfo = useUser();
    const selectedContent = videoCreator.selectedGeneratedContent[type];
    const {
      contentStudioEnableBlogSave,
      contentStudioEnableBlogShare,
      enableBlogRegeneration,
    } = useFlagsCombination(userIdentity.currentRole);

    const analyticsStore = useAnalytics();
    const transactionId = useRef<string | null>(null);
    useEffect(() => {
      transactionId.current = uuid().substring(0, 8);
    }, []);

    const savedContent =
      type === 'blog'
        ? videoCreator.story?.savedBlog
        : videoCreator.story?.savedSocialPost;
    const { current: contentProducer } = useRef(
      new BlogProducer(
        type,
        videoCreator,
        datoClientStore.assetRepository,
        datoClientStore.storyRepository,
      ),
    );

    const entries = contentProducer.getEntries();
    const [currentContent, setCurrentContent] =
      useState<BlogOrEmailContent | null>(null);

    const typeKey = type === 'blog' ? 'Blog' : 'Social Post';
    useEffect(() => {
      initializeContentStudioContent(videoCreator, story?.id, typeKey);
    }, [story?.id]);

    const data = videoCreator.contentStudioGeneratedContent?.[typeKey];

    const hasBeenGenerated = data?.hasBeenGenerated;
    let generatedContent = data?.content?.response || '';

    const handleCopyRichText = () => {
      if (!contentHtml) return;
      contentProducer.copyBlogContent(contentHtml);

      analyticsStore.trackEvent({
        logMethod: 'info',
        eventName: type === 'blog' ? 'copy_blog' : 'copy_social_post',
        label: type === 'blog' ? 'Copy blog' : 'Copy social post',
        transactionId: transactionId.current,
        extraProperties: {
          storyId: videoCreator.story?.id,
          storyTitle: videoCreator.story?.title,
        },
      });
    };

    const handleSave = async () => {
      if (!story?.id || !contentHtml) {
        return;
      }

      analytics.track(type === 'blog' ? 'save_blog' : 'save_social_post', {
        storyId: story.id,
        storyTitle: story.title,
      });

      setIsSaving(true);
      const name = userInfo?.user?.fullName;
      const data = await contentProducer.saveBlogContent(
        name,
        contentHtml,
        type === 'socialPost' && promptTemplate
          ? getSocialPostTitleFromTemplate(promptTemplate)
          : undefined,
      );

      if (data) {
        const { id, title, content, username } = data;
        setCurrentContent({
          id,
          title,
          content,
          username,
        });
        if (videoCreator.contentStudioGeneratedContent?.[typeKey]) {
          videoCreator.contentStudioGeneratedContent[typeKey].hasBeenGenerated =
            false;
        }
      }

      setIsSaving(false);
    };

    useEffect(() => {
      const generated_content = generatedContent as string;

      if (generatedContent && hasBeenGenerated) {
        const htmlData = contentProducer.produceHtmlFromGeneratedContent(
          generatedContent as string,
          editEnabled,
          imageSize,
          openDropdown,
          isResizing,
          dragPosition,
        );

        setContentHtml(htmlData);
        return;
      }

      if (currentContent?.id && savedContent) {
        const htmlData = contentProducer.produceHtmlFromSavedContent(
          currentContent.id,
          savedContent,
        );
        setContentHtml(htmlData || null);
      }

      if (currentContent?.id && savedContent) {
        const contentId = currentContent?.id;
        const data = savedContent?.[contentId]?.content;
        const hash = contentProducer.currentContentHash(
          generated_content,
          data,
        );

        setPageHash(hash);
      }
    }, [currentContent, savedContent, generatedContent]);

    const templatedPromptsStore = useTemplatedPrompts();
    const [aiProducerOpen, setAiProducerOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<
      AIPrompt | undefined
    >();
    const [customPrompt, setCustomPrompt] = useState<string | undefined>();
    const promptKey = type === 'blog' ? 'Blog' : 'Social Post';
    const handleGenerate = async (
      template: AIPrompt,
      brandVoice?: string,
      customInstructions?: string,
      avoidWords?: string,
      selectedSpeakers?: Speaker[],
    ) => {
      setImageSize(null);
      setIsLoading(true);
      analytics.track(
        type === 'blog' ? 'generate_blog' : 'generate_social_posts',
        {
          storyId: videoCreator.story?.id,
          storyTitle: videoCreator.story?.title,
          template,
          customInstructions,
          brandVoice,
        },
      );

      const contentTitle = type === 'blog' ? 'Blog' : 'Social Post';
      initializeContentStudioContent(
        videoCreator,
        videoCreator.story?.id,
        contentTitle,
      );

      await gptService.regenerateStreamResponse({
        key: promptKey,
        type: 'new',
        setLoading: setIsLoading,
        template,
        customInstructions,
        brandVoice,
        avoidWords,
        selectedSpeakers,
      });
      if (selectedSpeakers) setSelectedSpeakers(selectedSpeakers);
      if (customInstructions) setCustomPrompt(customInstructions);
      else if (template) setPromptTemplate(template);
    };

    const [revisionModalOpen, setRevisionModalOpen] = useState(false);
    const handleRegenerate = async (revision: string) => {
      setImageSize(null);
      setIsLoading(true);
      setRevisionModalOpen(false);
      analytics.track(
        type === 'blog' ? 'regenerate_blog' : 'regenerate_social_posts',
        {
          storyId: story?.id,
          storyTitle: videoCreator.story?.title,
          template: promptTemplate,
          customInstructions: customPrompt,
          revision,
        },
      );
      await gptService.regenerateStreamResponse({
        key: promptKey,
        type: 'regenerate',
        setLoading: setIsLoading,
        template: promptTemplate,
        customInstructions: customPrompt,
        revision,
      });
    };

    return (
      <>
        {isLoading && !hasBeenGenerated && (
          <SpinningLoading
            customStyle={{
              top: 0,
              position: 'fixed',
              alignItems: 'center',
            }}
            text={`Generating ${
              type === 'blog' ? 'article' : 'social post'
            }...`}
          />
        )}

        <Panel>
          <ContentActions
            hasBeenGenerated={!!selectedContent?.type}
            renderCreateNew={
              Boolean(entries?.length) || selectedContent
                ? () => (
                    <ContentSelector
                      entries={entries}
                      currentContent={currentContent}
                      setCurrentContent={setCurrentContent}
                      type={type === 'blog' ? 'savedBlog' : 'savedSocialPost'}
                      enableCreateAndEditing={contentStudioEnableBlogSave}
                      onCreateNew={() => setAiProducerOpen(true)}
                    />
                  )
                : undefined
            }
            onGenerate={() => setAiProducerOpen(true)}
            onRegenerate={() => setRevisionModalOpen(true)}
            renderShare={
              contentStudioEnableBlogShare && selectedContent?.type
                ? () => (
                    <ContentShare
                      type={type === 'blog' ? 'Blog' : 'Social Post'}
                      onCopy={handleCopyRichText}
                    />
                  )
                : undefined
            }
            onSave={
              contentStudioEnableBlogSave && selectedContent?.type
                ? handleSave
                : undefined
            }
            saveDisabled={isSaving}
            saveButtonLabel={isSaving ? 'Saving...' : 'Save'}
          />

          {Boolean(entries?.length) && !selectedContent && (
            <SavedBlogList
              entries={entries}
              currentContent={currentContent}
              setCurrentContent={setCurrentContent}
              type={type === 'blog' ? 'savedBlog' : 'savedSocialPost'}
            />
          )}

          {selectedContent && contentHtml ? (
            <Main>
              <Content>
                <BlogEditor
                  content={contentHtml}
                  onContentUpdate={(html: string) => {
                    setContentHtml(html);
                    videoCreator.contentStudioGeneratedContent = {
                      ...videoCreator.contentStudioGeneratedContent,
                      [typeKey]: {
                        ...data,
                        hasBeenGenerated: true,
                        content: {
                          ...(data?.content || {}),
                          response: html,
                        },
                      },
                    } as ContentViewData;
                  }}
                  disabled={isLoading}
                />
              </Content>
            </Main>
          ) : null}
        </Panel>

        {aiProducerOpen ? (
          <AIProducerModal
            customType={type === 'blog' ? 'Blog' : 'Social Post'}
            title={`Let's write ${
              type === 'blog' ? 'an article' : 'a social post'
            }`}
            templates={
              (type === 'blog'
                ? templatedPromptsStore.aiBlogTemplates
                : templatedPromptsStore.aiSocialPostTemplates) || []
            }
            onClose={() => setAiProducerOpen(false)}
            onConfirm={handleGenerate}
            enableSpeakerSelection={true}
          />
        ) : null}

        {revisionModalOpen ? (
          <BrandVoiceRevisionModal
            title={`Regenerate ${type === 'blog' ? 'Article' : 'Social Post'}`}
            onClose={() => setRevisionModalOpen(false)}
            handleGenerate={handleRegenerate}
          />
        ) : null}
      </>
    );
  },
);

export default BlogView;

const Panel = styled.div`
  .buttons {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
`;

const TopActions = styled.div`
  display: flex;
  gap: 5px;
  justify-content: space-between;
`;

const Create = styled.div`
  margin-top: 80px;
`;

const Main = styled.div`
  margin: 15px auto;
  position: relative;
  border-radius: 8px;
  border: 1px solid #484848;
  max-height: 800px;
  overflow: scroll;
`;
const Content = styled.div`
  color: #030419;
  margin: 40px auto;
  padding: 10px 50px;
  border-radius: 5px;
  user-select: none;
  max-width: 800px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  h1 {
    text-align: center;
  }
  p strong {
    font-weight: 800;
  }
  & > p:has(img) {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  blockquote {
    font-style: italic;
  }
  a {
    color: #030419;
  }
`;

const Save = styled.button`
  border: 0;
  outline: 0;
  background-color: #17c964;
  gap: 8px;
  width: 148px;
  height: 40px;
  border-radius: 10px;
  padding: 10px 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #03041a;
  cursor: pointer;
  transition: 0.25s;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
